import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";

import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import styles from "./EmailCaptureView.module.css";
import { useAppContext } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import { useCopy } from "../../context/CopyContext";
import Header from "../Header";
import Button from "../Button";
import PoweredBy from "../PoweredBy";
import Heading from "../Heading";
import ItemsModalView from "./ItemsModalView";

const KioskSignInView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { state, dispatch } = useAppContext();
  const { state: authState, login: tesLogin, handleCallback, clearError } = useAuth();
  const { getCopy } = useCopy();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [currentField, setCurrentField] = useState<'email' | 'password' | null>(null);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [layoutName, setLayoutName] = useState("default");
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);

  const keyboardRef = useRef<any>(null);
  const keyboardContainerRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  // Handle OAuth callback if code and state params are present
  useEffect(() => {
    const code = searchParams.get('code');
    const oauthState = searchParams.get('state');

    if (code && oauthState && !isProcessingCallback) {
      setIsProcessingCallback(true);
      const redirectUri = `${window.location.origin}${location.pathname}`;

      handleCallback(code, oauthState, redirectUri)
        .then(() => {
          // Clear URL params after successful callback
          window.history.replaceState({}, '', location.pathname);
        })
        .catch((error) => {
          console.error('OAuth callback failed:', error);
          window.history.replaceState({}, '', location.pathname);
        })
        .finally(() => {
          setIsProcessingCallback(false);
        });
    }
  }, [searchParams, location.pathname, handleCallback, isProcessingCallback]);

  // When TES auth succeeds, update app context and navigate
  useEffect(() => {
    if (authState.isAuthenticated && authState.user) {
      dispatch({ type: 'SET_EMAIL', payload: authState.user.email });
      dispatch({ type: 'SET_MEMBER', payload: true });
      dispatch({ type: 'SET_GUEST', payload: false });
      navigate("/reward-method-selection");
    }
  }, [authState.isAuthenticated, authState.user, dispatch, navigate]);

  // Get email from URL params if passed from QR code
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  // Don't show keyboard by default - only when focusing on fields
  
  // Debug: Log current state values
  useEffect(() => {
    console.log('Current state:', { email, password, currentField, showKeyboard });
  }, [email, password, currentField, showKeyboard]);

  const handleFieldFocus = (field: 'email' | 'password') => {
    console.log('Field focused:', field);
    setCurrentField(field);
    setShowKeyboard(true);
    
    // Small delay to ensure keyboard is rendered before setting input
    setTimeout(() => {
      if (keyboardRef.current) {
        const currentValue = field === 'email' ? email : password;
        console.log('Setting keyboard input to:', currentValue);
        keyboardRef.current.setInput(currentValue);
      }
    }, 150);
  };

  const onKeyboardChange = (input: string) => {
    console.log('Keyboard input changed:', { input, currentField });
    
    if (currentField === 'email') {
      console.log('Updating email to:', input);
      setEmail(input);
    } else if (currentField === 'password') {
      console.log('Updating password to:', input);
      setPassword(input);
    }
  };

  const onKeyPress = (button: string) => {
    console.log('Key pressed:', button);
    
    if (button === "{shift}" || button === "{lock}") {
      setLayoutName(layoutName === "default" ? "shift" : "default");
    }
    
    if (button === "{bksp}") {
      // Handle backspace
      if (currentField === 'email') {
        const newEmail = email.slice(0, -1);
        console.log('Backspace on email:', { from: email, to: newEmail });
        setEmail(newEmail);
        // Update keyboard display immediately
        setTimeout(() => {
          if (keyboardRef.current) {
            keyboardRef.current.setInput(newEmail);
          }
        }, 10);
      } else if (currentField === 'password') {
        const newPassword = password.slice(0, -1);
        console.log('Backspace on password:', { from: password, to: newPassword });
        setPassword(newPassword);
        // Update keyboard display immediately
        setTimeout(() => {
          if (keyboardRef.current) {
            keyboardRef.current.setInput(newPassword);
          }
        }, 10);
      }
    }
    
    if (button === "{enter}") {
      handleSignIn();
    }
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    const isOutsideKeyboard = keyboardContainerRef.current && !keyboardContainerRef.current.contains(target);
    
    // Check if click is outside both input fields
    const isOutsideEmailInput = emailInputRef.current && !emailInputRef.current.contains(target);
    const isOutsidePasswordInput = passwordInputRef.current && !passwordInputRef.current.contains(target);
    const isOutsideAllInputs = isOutsideEmailInput && isOutsidePasswordInput;
    
    console.log('Click outside check:', { isOutsideKeyboard, isOutsideAllInputs, showKeyboard });
    
    if (showKeyboard && isOutsideKeyboard && isOutsideAllInputs) {
      console.log('Hiding keyboard due to outside click');
      setShowKeyboard(false);
      setCurrentField(null);
    }
  }, [showKeyboard]);

  useEffect(() => {
    if (showKeyboard) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showKeyboard, handleClickOutside]);

  // Sync keyboard display with current field value when values change
  useEffect(() => {
    if (showKeyboard && keyboardRef.current && currentField) {
      const currentValue = currentField === 'email' ? email : password;
      console.log('Syncing keyboard with field value:', { currentField, currentValue, email, password });
      keyboardRef.current.setInput(currentValue);
    }
  }, [email, password, showKeyboard, currentField]);

  const handleSignIn = () => {
    if (!email.trim() || !password.trim()) {
      return;
    }
    
    // Mock sign in logic
    console.log("Kiosk sign in attempt:", { email, password });
    
    // Update app context with user info
    dispatch({ type: 'SET_EMAIL', payload: email });
    dispatch({ type: 'SET_MEMBER', payload: true });
    dispatch({ type: 'SET_GUEST', payload: false });
    
    // Navigate to reward method selection
    navigate("/reward-method-selection");
  };

  const handleSocialLogin = (provider: string) => {
    console.log(`${provider} login attempt from kiosk`);

    // Update app context with user info (use email from field or a placeholder)
    dispatch({ type: 'SET_EMAIL', payload: email || `user@${provider.toLowerCase()}.com` });
    dispatch({ type: 'SET_MEMBER', payload: true });
    dispatch({ type: 'SET_GUEST', payload: false });

    navigate("/reward-method-selection");
  };

  const handleTESLogin = async () => {
    console.log('TES OAuth login attempt from kiosk');
    clearError();
    const redirectUri = `${window.location.origin}${location.pathname}`;
    await tesLogin(redirectUri);
  };

  const handleBack = () => {
    navigate("/member-welcome");
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden">
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              
              
              
              
              
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Heading */}
          <div className="flex items-center justify-center">
            <div className="text-center w-full max-w-[920px]">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.6,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <Heading
                  title={getCopy('kioskSignIn.heading')}
                  subtitle={getCopy('kioskSignIn.subheading')}
                />
              </motion.div>
            </div>
          </div>

          {/* Rows 2-3 - Form Section spanning two rows */}
          <div className="row-span-2 flex items-center justify-center">
            <motion.div
              className="w-full max-w-[600px] flex flex-col justify-center space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.2,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              {/* Email Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.4,
                  delay: 0.3,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <input
                  ref={emailInputRef}
                  type="email"
                  value={email}
                  onChange={(e) => {
                    console.log('Email input changed via field:', e.target.value);
                    setEmail(e.target.value);
                  }}
                  onFocus={() => handleFieldFocus('email')}
                  autoComplete="off"
                  className={`w-full h-[88px] px-6 text-[32px] leading-[58px] font-light tracking-[-0.8px] text-center border-0 border-b-2 bg-transparent outline-none transition-colors duration-200 ${
                    currentField === 'email' 
                      ? "bg-brand-background border-brand-primary text-brand-text-primary placeholder-brand-text-muted" 
                      : "bg-brand-background border-brand-text-primary text-brand-text-primary placeholder-brand-text-muted focus:border-brand-primary"
                  }`}
                  placeholder="Email address"
                  style={{
                    fontFamily: "var(--brand-font-primary), sans-serif",
                    WebkitBoxShadow: '0 0 0 1000px transparent inset',
                    WebkitTextFillColor: 'inherit',
                    backgroundColor: 'transparent',
                  }}
                  required
                />
              </motion.div>

              {/* Password Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.4,
                  delay: 0.4,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <input
                  ref={passwordInputRef}
                  type="password"
                  value={password}
                  onChange={(e) => {
                    console.log('Password input changed via field:', e.target.value);
                    setPassword(e.target.value);
                  }}
                  onFocus={() => handleFieldFocus('password')}
                  autoComplete="off"
                  className={`w-full h-[88px] px-6 text-[32px] leading-[58px] font-light tracking-[-0.8px] text-center border-0 border-b-2 bg-transparent outline-none transition-colors duration-200 ${
                    currentField === 'password' 
                      ? "bg-brand-background border-brand-primary text-brand-text-primary placeholder-brand-text-muted" 
                      : "bg-brand-background border-brand-text-primary text-brand-text-primary placeholder-brand-text-muted focus:border-brand-primary"
                  }`}
                  placeholder="Password"
                  style={{
                    fontFamily: "var(--brand-font-primary), sans-serif",
                    WebkitBoxShadow: '0 0 0 1000px transparent inset',
                    WebkitTextFillColor: 'inherit',
                    backgroundColor: 'transparent',
                  }}
                  required
                />
              </motion.div>

              {/* Auth Error Display */}
              {authState.error && (
                <motion.div
                  className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <p className="font-brand-primary text-[18px]">{authState.error}</p>
                  <button
                    onClick={clearError}
                    className="text-sm underline mt-2"
                  >
                    Dismiss
                  </button>
                </motion.div>
              )}

              {/* Social Login Options - animated visibility when keyboard is active */}
              <AnimatePresence>
                {!showKeyboard && (
                  <motion.div
                    className="pt-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{
                      duration: 0.3,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <p className="text-center font-brand-primary text-[24px] mb-6">Or sign in with</p>

                    <div className="flex flex-col gap-4">
                      {/* TES/Pentatonic OAuth Login */}
                      <Button
                        variant="tertiary"
                        onClick={handleTESLogin}
                        disabled={authState.isLoading || isProcessingCallback}
                      >
                        <div className="flex items-center gap-3">
                          {authState.isLoading || isProcessingCallback ? (
                            <>
                              <svg className="animate-spin w-6 h-6" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              <span>Signing in...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                              </svg>
                              <span>Pentatonic Account</span>
                            </>
                          )}
                        </div>
                      </Button>

                      <Button variant="tertiary" onClick={() => handleSocialLogin('Facebook')}>
                        <div className="flex items-center gap-3">
                          <img src="/assets/images/icons/facebook-icon.png" alt="Facebook" className="w-6 h-6" />
                          <span>Facebook</span>
                        </div>
                      </Button>
                      <Button variant="tertiary" onClick={() => handleSocialLogin('Google')}>
                        <div className="flex items-center gap-3">
                          <img src="/assets/images/icons/google-icon.png" alt="Google" className="w-6 h-6" />
                          <span>Google</span>
                        </div>
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom Section */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Back Button */}
          <div className="flex justify-center w-full max-w-[282px]">
            <Button variant="tertiary" onClick={handleBack}>
              Back
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Virtual Keyboard */}
      <AnimatePresence>
        {showKeyboard && currentField && (
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-brand-background"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{
              duration: 0.2,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            ref={keyboardContainerRef}
          >
            <div className={styles.keyboardContainer}>
              <Keyboard
                ref={keyboardRef}
                layoutName={layoutName}
                onChange={onKeyboardChange}
                onKeyPress={onKeyPress}
                theme="hg-theme-default hg-layout-default"
                layout={{
                  default: [
                    "1 2 3 4 5 6 7 8 9 0 {bksp}",
                    "q w e r t y u i o p",
                    "a s d f g h j k l",
                    "{shift} z x c v b n m {shift}",
                    "{space} @ . {enter}"
                  ],
                  shift: [
                    "! @ # $ % ^ & * ( ) {bksp}",
                    "Q W E R T Y U I O P",
                    "A S D F G H J K L",
                    "{shift} Z X C V B N M {shift}",
                    "{space} @ . {enter}"
                  ]
                }}
                display={{
                  '{bksp}': '⌫',
                  '{enter}': 'Sign In',
                  '{shift}': '⇧',
                  '{space}': 'Space',
                  '@': '@',
                  '.': '.'
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default KioskSignInView;
